/**
 * ReceiptScanScreen — pixel-faithful port of `design/project/screens-receipt-scan.jsx`.
 *
 * Flow:
 *  1. User picks 1..10 images via camera (`capture=environment`) or gallery
 *     (`multiple`).
 *  2. Each image is downscaled (`downscaleImage`) and the batch is sent to
 *     `ocrRecognize` as a single request.
 *  3. Per-image card displays scanning / done / failed state. After 3s of
 *     waiting, an elapsed-second counter appears next to "辨識中".
 *  4. Each `ok` result is editable (date / merchant / amount / currency /
 *     category / book) before saving.
 *  5. "全部記入" enqueues `create_tx` for each `ok`, skips `failed`, toasts a
 *     summary, then closes.
 *
 * Sharp edges:
 *  - We NEVER log the base64 image. Only counts and statuses.
 *  - Non-image MIME types are rejected by `downscaleImage` with `NotAnImageError`
 *    and surfaced as a toast — do not pass arbitrary files to OCR.
 *  - Picking 11+ images blocks the entire upload (toast: "最多 10 張，已捨棄 X 張").
 *    Silently truncating to first 10 hides intent. The user gets clear feedback.
 *  - Cold-start counter starts at the 3s threshold using `setInterval(1000)`,
 *    not earlier — keeps fast paths quiet.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import Icon from '@/components/common/Icon';
import Toast, { type ToastVariant } from '@/components/common/Toast';
import BottomSheet from '@/components/common/BottomSheet';
import {
  ocrRecognize,
  OcrRequestError,
  type OcrResult,
  type OcrResultOk,
  type OcrImage,
  type OcrCategory,
  type OcrFields,
} from '@/lib/ocr/client';
import {
  downscaleImage,
  ImageTooLargeError,
  NotAnImageError,
} from '@/lib/ocr/downscale';
import { useStore } from '@/state/store';
import { enqueueOp } from '@/lib/sheets/syncWiring';
import { formatAmount } from '@/lib/format/currency';
import { convertMinor } from '@/lib/format/fx';
import { joinDateTime, dayPart, timePart } from '@/lib/format/datetime';
import CategoryPicker from '@/components/transactions/CategoryPicker';
import type { TxKind } from '@/lib/sheets/types';

const MAX_IMAGES = 10;
const COLD_START_THRESHOLD_MS = 3000;
const SUPPORTED_CURRENCIES = ['TWD', 'JPY', 'USD'] as const;
type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

type ItemStatus = 'scanning' | 'done' | 'failed';

interface EditableFields {
  date: string;
  merchant: string;
  amountMinor: number;
  currency: SupportedCurrency;
  categoryId: string;
  subCategoryId: string;
  bookId: string;
  /**
   * Pre-formatted note shown verbatim in the confirm sheet. Default is
   * `${merchant}`.
   * The user can edit this freely — what's in this field is exactly what
   * lands on the tx row's `note` column.
   */
  note: string;
}

interface ReceiptItem {
  /** Stable id used as OCR client_id. */
  id: string;
  fileName: string;
  status: ItemStatus;
  startedAt: number;
  /** Filled after OCR returns. */
  fields?: EditableFields;
  /** Failure reason for `failed` items. */
  error?: string;
  /**
   * Original File object retained so the user can retry recognition on a
   * single failed card. Browser File handles are valid until GC; we keep
   * one reference per item until removed.
   */
  file?: File;
  /**
   * Inline data URL of the downscaled image (the same bytes sent to
   * Gemini). Used to render a thumbnail in the row + the confirm sheet
   * so the user can verify which receipt they're editing without
   * scrolling back to Photos.
   */
  thumbnailDataUrl?: string;
}

export interface ReceiptScanScreenProps {
  onClose: () => void;
}

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `rcpt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Current local time as `HH:MM`. Used as the fallback purchase time when the
 * receipt has no readable printed time, so OCR transactions carry a `THH:MM`
 * component and sort consistently with manual entries (which always do). */
function nowHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function defaultBookId(): string | null {
  return useStore.getState().books.activeBookId;
}

function normalizeCurrency(c: string | undefined): SupportedCurrency {
  const up = (c ?? '').toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(up)
    ? (up as SupportedCurrency)
    : 'TWD';
}

/**
 * Build a multi-line bullet list of receipt line items. Used to pre-fill
 * the tx note in the format the user requested:
 *
 *   - 牛丼（牛肉蓋飯） x1 @290
 *   - 蝦仁起司滑嫩蛋 x1 @360
 *
 * Foreign-language item names carry a zh-TW translation in `name_local`,
 * rendered as `原文（中文）` so the user can read the line without losing the
 * original receipt text. The `（中文）` fragment is omitted when the
 * translation is empty (already Chinese / untranslatable) OR when it is a
 * verbatim copy of the original (no information added).
 *
 * Skips lines with no name. Quantity defaults to 1 when missing; price
 * fragment is omitted when 0/missing (avoids "@0" noise on rough OCR
 * results). User can edit the whole block in the confirm sheet.
 */
type CategoryLike = {
  id: string;
  name: string;
  parent_id: string;
  deleted_at: string;
};

/**
 * Build the expense taxonomy sent to the OCR backend: the categories actually
 * visible in the active book (system globals with empty book_id, plus this
 * book's own custom categories), expense kind, not deleted. Sent as
 * {id, name, parent_id} so the model picks a real id — including renamed and
 * custom (UUID) sub-categories the old hardcoded slug vocabulary could never
 * represent.
 */
function buildExpenseTaxonomy(
  categories: ReturnType<typeof useStore.getState>['categories']['items'],
  activeBookId: string,
): OcrCategory[] {
  return categories
    .filter(
      (c) =>
        !c.deleted_at &&
        c.kind === 'expense' &&
        (c.book_id === '' || c.book_id === activeBookId),
    )
    .map((c) => ({ id: c.id, name: c.name, parent_id: c.parent_id }));
}

/** Accept the model's category_id only when it's a real, non-deleted main
 * category in the user's list. The backend already constrained it; this is the
 * front-end's defence-in-depth. */
function resolveCategoryId(id: string, categories: CategoryLike[]): string {
  if (!id) return '';
  const cat = categories.find((c) => c.id === id && !c.deleted_at && !c.parent_id);
  return cat?.id ?? '';
}

/** Accept the model's subcategory_id only when it exists, is not deleted, and
 * its parent equals the resolved category — blocks a sub from a different
 * category leaking through. */
function resolveSubCategoryId(
  id: string,
  parentCategoryId: string,
  categories: CategoryLike[],
): string {
  if (!id || !parentCategoryId) return '';
  const cat = categories.find(
    (c) => c.id === id && !c.deleted_at && c.parent_id === parentCategoryId,
  );
  return cat?.id ?? '';
}

/**
 * Build EditableFields from one Gemini receipt entry. Centralised so the
 * multi-receipt expand path and any future single-receipt callers stay
 * in sync.
 */
function buildFields(
  rec: OcrFields,
  activeBook: string,
  categories: CategoryLike[],
): EditableFields {
  const merchant = rec.merchant || '';
  const note = merchant;
  // Use the model's real ids directly (re-validated against the live list).
  const categoryId = resolveCategoryId(rec.category_id, categories);
  const subCategoryId = resolveSubCategoryId(
    rec.subcategory_id,
    categoryId,
    categories,
  );
  return {
    // Always carry a time component: use the receipt's printed time when
    // present, otherwise the capture time. Without this, OCR rows stored as
    // date-only (`YYYY-MM-DD`) tie-break inconsistently against manual entries
    // (`YYYY-MM-DDTHH:MM`) and clump at one edge of their day-group.
    date: joinDateTime(rec.date || todayIsoDate(), rec.time || nowHm()),
    merchant,
    amountMinor: rec.amount_minor || 0,
    currency: normalizeCurrency(rec.currency),
    categoryId,
    subCategoryId,
    bookId: activeBook,
    note,
  };
}

export default function ReceiptScanScreen({
  onClose,
}: ReceiptScanScreenProps): JSX.Element {
  const [items, setItems] = useState<ReceiptItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    variant: ToastVariant;
  } | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  // Force re-render on a tick so the elapsed counter ticks visibly.
  const [, setTick] = useState(0);

  // Single image source: one gallery-style file input. On iOS the OS-native
  // picker this opens already offers both camera and photo library, so a
  // separate in-app "拍照" action is redundant — and worse, having one made
  // users mis-tap camera when they meant to pick from the library. There is
  // no web attribute to restrict the iOS picker to library-only, so the fix
  // is to collapse to a single entry point rather than add more buttons.
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const openPicker = (): void => galleryInputRef.current?.click();

  const categories = useStore((s) => s.categories.items);
  const books = useStore((s) => s.books.items);
  // NOT useStore((s) => s.sync.enqueue) — that mutates the local slice
  // only and the queue worker never sees the op. Use the canonical
  // enqueueOp from syncWiring so the create_tx actually reaches Sheets.
  // Same bug pattern that bit AddTxScreen in earlier dogfood rounds.

  // Lock background scroll while this full-screen overlay is open. Without
  // this the page underneath scrolls behind the overlay (and iOS rubber-bands
  // the whole document). Restore the prior values on unmount so other screens
  // are unaffected.
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  // Tick once per second whenever there's a scanning item that's been waiting > 3s.
  // Sharp edge: we only start the interval after the threshold to avoid noise on
  // fast paths.
  useEffect(() => {
    const slowOne = items.find(
      (it) =>
        it.status === 'scanning' &&
        Date.now() - it.startedAt >= COLD_START_THRESHOLD_MS,
    );
    if (!slowOne) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [items]);

  const showToast = (message: string, variant: ToastVariant = 'info'): void => {
    setToast({ message, variant });
  };

  const closeToast = (): void => setToast(null);

  // ─── File handling ─────────────────────────────────────────────────────
  const handleFiles = async (selected: FileList | null): Promise<void> => {
    if (!selected || selected.length === 0) return;
    const list = Array.from(selected);

    if (list.length > MAX_IMAGES) {
      showToast(`最多 10 張，已捨棄 ${list.length - MAX_IMAGES} 張`, 'warn');
      return; // block entirely (documented decision)
    }

    if (items.length + list.length > MAX_IMAGES) {
      const overflow = items.length + list.length - MAX_IMAGES;
      showToast(`最多 10 張，已捨棄 ${overflow} 張`, 'warn');
      return;
    }

    const startedAt = Date.now();

    // Reserve placeholder items so the UI shows scanning thumbnails immediately.
    // We retain the File reference so a failed card can re-trigger OCR
    // without needing a fresh user pick.
    const placeholders: ReceiptItem[] = list.map((f) => ({
      id: genId(),
      fileName: f.name,
      status: 'scanning' as const,
      startedAt,
      file: f,
    }));
    setItems((prev) => [...prev, ...placeholders]);

    // Downscale each image. If any fail, mark that placeholder failed and
    // exclude from the OCR batch — the rest still go.
    const ocrInputs: OcrImage[] = [];
    const inputIds: string[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list[i]!;
      const ph = placeholders[i]!;
      try {
        const ds = await downscaleImage(file);
        ocrInputs.push({ id: ph.id, data: ds.data, mime: ds.mime });
        inputIds.push(ph.id);
        // Stash the downscaled bytes as a data URL so the row + confirm
        // sheet can render a thumbnail. Same bytes Gemini sees, so the
        // thumbnail accurately represents what was OCR'd.
        const thumbnailDataUrl = `data:${ds.mime};base64,${ds.data}`;
        setItems((prev) =>
          prev.map((it) =>
            it.id === ph.id ? { ...it, thumbnailDataUrl } : it,
          ),
        );
      } catch (err) {
        const reason =
          err instanceof NotAnImageError
            ? '不支援的檔案類型'
            : err instanceof ImageTooLargeError
              ? '圖片過大，請使用較小的版本'
              : '圖片處理失敗';
        // Replace placeholder with failed status. Do NOT log the file content.
        setItems((prev) =>
          prev.map((it) =>
            it.id === ph.id
              ? { ...it, status: 'failed', error: reason }
              : it,
          ),
        );
        showToast(reason, 'error');
      }
    }

    if (ocrInputs.length === 0) return;

    const taxonomy: OcrCategory[] = buildExpenseTaxonomy(
      categories,
      defaultBookId() ?? '',
    );

    let results: OcrResult[];
    try {
      results = await ocrRecognize(ocrInputs, taxonomy);
    } catch (err) {
      // Mark the in-flight ones failed. Do NOT log image data.
      const msg =
        err instanceof Error ? err.message : 'OCR request failed';
      // Kind-aware toast (Section 3): forbidden = email not on allowlist,
      // unauthorized/401 = re-login. Falls back to generic for unknown.
      let toastMsg = 'OCR 服務暫時無法使用';
      let cardError = 'OCR 服務暫時無法使用';
      if (err instanceof OcrRequestError) {
        if (err.reason === 'forbidden' || err.status === 403) {
          toastMsg = '你的 email 未在 OCR 允許名單，請聯絡管理員加入。';
          cardError = '帳號未在 OCR 允許名單';
        } else if (err.reason === 'unauthorized' || err.status === 401) {
          toastMsg = '存取權杖已過期，請重新登入。';
          cardError = '需要重新登入';
        } else if (err.status === 429 || err.reason === 'rate_limited') {
          toastMsg = '辨識請求過於頻繁，請稍後再試。';
          cardError = '辨識請求過於頻繁';
        }
      }
      setItems((prev) =>
        prev.map((it) =>
          inputIds.includes(it.id)
            ? { ...it, status: 'failed' as const, error: cardError }
            : it,
        ),
      );
      // eslint-disable-next-line no-console
      console.warn('[ReceiptScan] OCR request failed:', msg);
      showToast(toastMsg, 'error');
      return;
    }

    // Match results back to items by id. flatMap because one image may
    // expand into multiple receipts (e.g. user photographed a stack of
    // receipts in one shot — each becomes its own ReceiptItem with the
    // shared thumbnail).
    const activeBook = defaultBookId() ?? '';
    setItems((prev) =>
      prev.flatMap((it): ReceiptItem[] => {
        if (!inputIds.includes(it.id)) return [it];
        const r = results.find((x) => x.id === it.id);
        if (!r) {
          return [{ ...it, status: 'failed', error: '未收到辨識結果' }];
        }
        if (r.status === 'failed') {
          return [{ ...it, status: 'failed', error: r.error }];
        }
        const ok = r as OcrResultOk;
        return ok.receipts.map((rec, idx) => ({
          ...it,
          // Suffix the id when there's more than one receipt — IDs must
          // stay unique in the items array (React keys + dedup logic).
          id: ok.receipts.length === 1 ? it.id : `${it.id}#${idx}`,
          status: 'done' as const,
          fields: buildFields(rec, activeBook, categories),
        }));
      }),
    );
  };

  // ─── Editing ───────────────────────────────────────────────────────────
  const updateFields = (id: string, patch: Partial<EditableFields>): void => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id && it.fields
          ? { ...it, fields: { ...it.fields, ...patch } }
          : it,
      ),
    );
  };

  const removeItem = (id: string): void => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const retryItem = async (id: string): Promise<void> => {
    const target = items.find((it) => it.id === id);
    if (!target || !target.file) {
      showToast('找不到原圖檔，請重新加入這張收據', 'warn');
      return;
    }
    const startedAt = Date.now();
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, status: 'scanning' as const, startedAt, error: undefined }
          : it,
      ),
    );

    let ds: Awaited<ReturnType<typeof downscaleImage>>;
    try {
      ds = await downscaleImage(target.file);
    } catch (err) {
      const reason =
        err instanceof NotAnImageError
          ? '不支援的檔案類型'
          : err instanceof ImageTooLargeError
            ? '圖片過大，請使用較小的版本'
            : '圖片處理失敗';
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, status: 'failed', error: reason }
            : it,
        ),
      );
      showToast(reason, 'error');
      return;
    }

    const taxonomy: OcrCategory[] = buildExpenseTaxonomy(
      categories,
      defaultBookId() ?? '',
    );

    let results: OcrResult[];
    try {
      results = await ocrRecognize([{ id, data: ds.data, mime: ds.mime }], taxonomy);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'OCR request failed';
      let toastMsg = 'OCR 服務暫時無法使用';
      let cardError = 'OCR 服務暫時無法使用';
      if (err instanceof OcrRequestError) {
        if (err.reason === 'forbidden' || err.status === 403) {
          toastMsg = '你的 email 未在 OCR 允許名單，請聯絡管理員加入。';
          cardError = '帳號未在 OCR 允許名單';
        } else if (err.reason === 'unauthorized' || err.status === 401) {
          toastMsg = '存取權杖已過期，請重新登入。';
          cardError = '需要重新登入';
        } else if (err.status === 429 || err.reason === 'rate_limited') {
          toastMsg = '辨識請求過於頻繁，請稍後再試。';
          cardError = '辨識請求過於頻繁';
        }
      }
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, status: 'failed' as const, error: cardError }
            : it,
        ),
      );
      // eslint-disable-next-line no-console
      console.warn('[ReceiptScan] OCR retry failed:', msg);
      showToast(toastMsg, 'error');
      return;
    }

    const r = results.find((x) => x.id === id);
    const activeBook = defaultBookId() ?? '';
    // Single-card retry path: same multi-receipt expansion as the batch
    // path. The retried card's id might fan out into N cards if the user
    // photographed multiple receipts in one shot.
    setItems((prev) =>
      prev.flatMap((it): ReceiptItem[] => {
        if (it.id !== id) return [it];
        if (!r) return [{ ...it, status: 'failed', error: '未收到辨識結果' }];
        if (r.status === 'failed') {
          return [{ ...it, status: 'failed', error: r.error }];
        }
        const ok = r as OcrResultOk;
        return ok.receipts.map((rec, idx) => ({
          ...it,
          id: ok.receipts.length === 1 ? it.id : `${it.id}#${idx}`,
          status: 'done' as const,
          fields: buildFields(rec, activeBook, categories),
        }));
      }),
    );
  };

  // ─── Batch save ────────────────────────────────────────────────────────
  const okItems = useMemo(
    () => items.filter((it) => it.status === 'done' && it.fields),
    [items],
  );
  const failedCount = items.filter((it) => it.status === 'failed').length;
  const scanningCount = items.filter((it) => it.status === 'scanning').length;

  // Sum of all done items in the *primary currency*. Mixed currencies are
  // shown as their own group; we only sum items that match `primaryCcy`.
  // The design summary always renders "合計 NT$ …" so we surface the
  // active book's currency here. (When all items are TWD this just works;
  // mixed-currency batches report the dominant currency's subtotal.)
  const primaryCcy: SupportedCurrency = okItems[0]?.fields?.currency ?? 'TWD';
  const totalAmtMinor = okItems
    .filter((it) => it.fields?.currency === primaryCcy)
    .reduce((sum, it) => sum + (it.fields?.amountMinor ?? 0), 0);

  const handleSaveAll = (): void => {
    if (savingAll) return;
    if (okItems.length === 0) return;
    setSavingAll(true);
    let saved = 0;
    let skipped = 0;
    const fallbackBook = defaultBookId() ?? '';
    for (const it of okItems) {
      const f = it.fields!;
      // Fill missing fields with safe defaults so the user's intent is
      // honoured even when the OCR prompt didn't return everything.
      // Previously a missing categoryId caused the whole row to be
      // silently dropped — user pressed "全部記入" and saw "已記入 0 筆"
      // with no clue why.
      const bookId = f.bookId || fallbackBook;
      const categoryId = f.categoryId || 'cat-other';
      if (!bookId) {
        skipped += 1;
        // eslint-disable-next-line no-console
        console.warn('[ReceiptScan] skipping row — no active book', { id: it.id });
        continue;
      }
      const kind: TxKind = 'expense';
      const txId = genId();
      const nowIso = new Date().toISOString();
      // f.note is the user-editable note from the confirm sheet (pre-filled
      // with merchant + bullet list of items). Falls back to merchant when
      // the user wiped the note entirely.
      const note = f.note || f.merchant;
      const txRow = {
        id: txId,
        book_id: bookId,
        date: f.date,
        amount_minor: f.amountMinor,
        currency: f.currency,
        kind,
        category_id: categoryId,
        subcategory_id: f.subCategoryId || '',
        note,
        project: '',
        tags: '',
        recurring_id: '',
        rev: 1,
        created_at: nowIso,
        updated_at: nowIso,
        deleted_at: '',
      };
      // Optimistic local insert so Home / Analytics show the row
      // immediately, before the queue flushes to Sheets. Without this
      // the user sees the "已記入" toast, navigates home, and stares at
      // an empty list for ~30s while the worker drains.
      useStore.getState().transactions.upsert(txRow);
      // enqueueOp is the worker-driven path (syncWiring). The slice's
      // sync.enqueue is local-state only and the queue worker never
      // observes it — that's why earlier OCR runs "succeeded" in the UI
      // but never appeared in Sheets.
      enqueueOp({
        type: 'create_tx',
        payload: txRow,
      });
      saved += 1;
    }
    setSavingAll(false);
    const parts: string[] = [`已記入 ${saved} 筆`];
    if (skipped > 0) parts.push(`略過 ${skipped} 筆 (沒有可用帳本)`);
    if (failedCount > 0) parts.push(`跳過 ${failedCount} 筆 (辨識失敗)`);
    showToast(parts.join('，'), saved > 0 ? 'success' : 'error');
    // Auto-close after a brief delay so the toast can be read.
    window.setTimeout(onClose, 1200);
  };

  const canSave = okItems.length > 0 && scanningCount === 0 && !savingAll;
  const editing = editingId ? items.find((it) => it.id === editingId) : null;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        background: 'var(--cream-50)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '54px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--cream-50)',
          borderBottom: '0.5px solid var(--border-1)',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="返回"
          style={iconButtonStyle()}
        >
          <Icon name="chevL" size={22} stroke="var(--fg-1)" />
        </button>
        <h1
          style={{
            font: '600 16px var(--font-cjk)',
            color: 'var(--fg-1)',
            margin: 0,
          }}
        >
          掃描收據
        </h1>
        <button
          type="button"
          onClick={openPicker}
          aria-label="加入收據"
          style={iconButtonStyle()}
        >
          <Icon name="plus" size={22} stroke="var(--fg-1)" />
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {items.length === 0 ? (
          <ReceiptEmpty onPick={openPicker} />
        ) : (
          <ReceiptList
            items={items}
            categories={categories}
            onEdit={(id) => setEditingId(id)}
            onRemove={removeItem}
            onRetry={(id) => {
              void retryItem(id);
            }}
            onAddMore={openPicker}
          />
        )}
      </div>

      {/* Bottom save bar */}
      {items.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            padding: '12px 16px 28px',
            background: 'var(--cream-50)',
            borderTop: '0.5px solid var(--border-1)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '500 11px var(--font-cjk)',
                color: 'var(--fg-3)',
                letterSpacing: '0.04em',
              }}
            >
              {scanningCount > 0
                ? `辨識中 ${scanningCount} / ${items.length}`
                : `已辨識 ${okItems.length} 張${failedCount ? ` · 失敗 ${failedCount}` : ''}`}
            </div>
            {okItems.length > 0 && (
              <div
                style={{
                  font: '700 17px var(--font-mono, var(--font-num))',
                  color: 'var(--fg-1)',
                  marginTop: 2,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                合計 {formatAmount(totalAmtMinor, primaryCcy)}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSaveAll}
            style={{
              padding: '12px 22px',
              borderRadius: 14,
              border: 0,
              cursor: canSave ? 'pointer' : 'not-allowed',
              background: canSave ? 'var(--accent)' : 'var(--sand-300)',
              color: '#fff',
              font: '600 14px var(--font-cjk)',
            }}
          >
            {savingAll ? '儲存中…' : `全部記入 ${okItems.length}`}
          </button>
        </div>
      )}

      {/* Single hidden file input. `accept="image/*"` lets the OS picker
       * offer camera + library on its own; we deliberately do NOT add a
       * `capture` input, since a dedicated camera entry caused mis-taps. */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFiles(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />

      {editing && editing.fields && (
        <ReceiptEditSheet
          item={editing}
          fields={editing.fields}
          categories={categories}
          books={books}
          onSave={(patch) => {
            updateFields(editing.id, patch);
            setEditingId(null);
          }}
          onClose={() => setEditingId(null)}
        />
      )}

      {toast && (
        <Toast
          open
          message={toast.message}
          variant={toast.variant}
          onClose={closeToast}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────────────

function ReceiptEmpty({ onPick }: { onPick: () => void }): JSX.Element {
  return (
    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
      <div
        style={{
          width: 120,
          height: 120,
          margin: '10px auto 22px',
          borderRadius: 32,
          background: 'linear-gradient(155deg, var(--cream-200), var(--sand-200))',
          display: 'grid',
          placeItems: 'center',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <Icon name="camera" size={50} stroke="var(--mocha-700)" />
      </div>
      <div
        style={{
          font: '600 19px var(--font-cjk)',
          color: 'var(--fg-1)',
          marginBottom: 8,
        }}
      >
        一次拍多張收據
      </div>
      <div
        style={{
          font: '400 13.5px var(--font-cjk)',
          color: 'var(--fg-2)',
          lineHeight: 1.7,
          maxWidth: 280,
          margin: '0 auto 26px',
        }}
      >
        系統自動辨識金額、店家、日期，並建議分類。
        <br />
        確認後一鍵全部記入。
      </div>
      <button
        type="button"
        onClick={onPick}
        style={{
          padding: '14px 28px',
          borderRadius: 14,
          border: 0,
          background: 'var(--accent)',
          color: '#fff',
          font: '600 14px var(--font-cjk)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <Icon name="camera" size={18} stroke="#fff" /> 開始掃描
      </button>
    </div>
  );
}

interface ReceiptListProps {
  items: ReceiptItem[];
  categories: ReturnType<typeof useStore.getState>['categories']['items'];
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  onAddMore: () => void;
}

function ReceiptList({
  items,
  categories,
  onEdit,
  onRemove,
  onRetry,
  onAddMore,
}: ReceiptListProps): JSX.Element {
  return (
    <div style={{ padding: '14px 16px 24px' }}>
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 12,
          background: 'var(--cream-200)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <Icon name="check" size={16} stroke="var(--mocha-700)" />
        <div
          style={{
            font: '400 12px var(--font-cjk)',
            color: 'var(--mocha-700)',
            lineHeight: 1.55,
          }}
        >
          點縮圖編輯辨識結果，確認無誤後按「全部記入」。
        </div>
      </div>

      {items.map((r) => (
        <ReceiptCard
          key={r.id}
          item={r}
          categories={categories}
          onEdit={() => onEdit(r.id)}
          onRemove={() => onRemove(r.id)}
          onRetry={() => onRetry(r.id)}
        />
      ))}

      <button
        type="button"
        onClick={onAddMore}
        style={{
          width: '100%',
          padding: 14,
          marginTop: 6,
          borderRadius: 14,
          border: '1.5px dashed var(--border-2)',
          background: 'transparent',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          font: '500 13px var(--font-cjk)',
          color: 'var(--fg-2)',
        }}
      >
        <Icon name="plus" size={16} stroke="var(--fg-2)" /> 加入更多收據
      </button>
    </div>
  );
}

function ReceiptCard({
  item,
  categories,
  onEdit,
  onRemove,
  onRetry,
}: {
  item: ReceiptItem;
  categories: ReturnType<typeof useStore.getState>['categories']['items'];
  onEdit: () => void;
  onRemove: () => void;
  onRetry: () => void;
}): JSX.Element {
  const isScanning = item.status === 'scanning';
  const isDone = item.status === 'done';
  const isFailed = item.status === 'failed';

  // Cold-start counter: only show after threshold to avoid noise on fast paths.
  const elapsedMs = isScanning ? Date.now() - item.startedAt : 0;
  const showElapsed = elapsedMs >= COLD_START_THRESHOLD_MS;
  const elapsedSec = Math.floor(elapsedMs / 1000);

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        marginBottom: 10,
        background: 'var(--bg-surface)',
        borderRadius: 14,
        border: '0.5px solid var(--border-1)',
        boxShadow: 'var(--shadow-1)',
        opacity: isScanning ? 0.78 : 1,
      }}
    >
      {/* Thumbnail — shows downscaled image once available, falls back to
       * a warm gradient placeholder while the file is still being processed.
       * The same data URL is used in the confirm sheet so user always sees
       * which receipt they're looking at. */}
      <button
        type="button"
        onClick={isDone ? onEdit : undefined}
        aria-label={isDone ? '編輯辨識結果' : '收據縮圖'}
        style={{
          flexShrink: 0,
          width: 74,
          height: 96,
          borderRadius: 10,
          overflow: 'hidden',
          position: 'relative',
          border: 0,
          padding: 0,
          background: item.thumbnailDataUrl
            ? `center/cover no-repeat url(${item.thumbnailDataUrl})`
            : 'linear-gradient(155deg,#fff8ec 0%,#f6e6c4 60%,#e9cf99 100%)',
          cursor: isDone ? 'pointer' : 'default',
          boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.08)',
        }}
      >
        {isScanning && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.42)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                animation: 'mochia-spin 0.8s linear infinite',
              }}
            />
          </div>
        )}
      </button>

      {/* Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {isScanning ? (
          <div>
            <div
              style={{
                font: '500 14px var(--font-cjk)',
                color: 'var(--fg-2)',
                marginBottom: 4,
              }}
            >
              {showElapsed ? `辨識中… ${elapsedSec}s` : '辨識中…'}
            </div>
            <div
              style={{ font: '400 12px var(--font-cjk)', color: 'var(--fg-3)' }}
            >
              正在分析金額、店家與日期
            </div>
          </div>
        ) : isFailed ? (
          <div>
            <div
              style={{
                font: '500 14px var(--font-cjk)',
                color: '#c45a4a',
                marginBottom: 4,
              }}
            >
              辨識失敗
            </div>
            <div
              style={{
                font: '400 12px var(--font-cjk)',
                color: 'var(--fg-3)',
                lineHeight: 1.5,
              }}
            >
              {item.error || '請重新拍攝或更換圖片'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {item.file && (
                <button
                  type="button"
                  onClick={onRetry}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 8,
                    background: 'var(--accent)',
                    color: '#fff',
                    border: 0,
                    cursor: 'pointer',
                    font: '500 12px var(--font-cjk)',
                  }}
                >
                  重新辨識
                </button>
              )}
              <button
                type="button"
                onClick={onRemove}
                style={{
                  padding: '5px 12px',
                  borderRadius: 8,
                  background: 'transparent',
                  color: 'var(--fg-3)',
                  border: '0.5px solid var(--border-1)',
                  cursor: 'pointer',
                  font: '500 12px var(--font-cjk)',
                }}
              >
                移除
              </button>
            </div>
          </div>
        ) : (
          <DoneFieldsSummary fields={item.fields!} categories={categories} />
        )}
      </div>

      {/* Actions */}
      {isDone && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onEdit}
            aria-label="編輯"
            style={iconButtonStyle(30)}
          >
            <Icon name="edit" size={15} stroke="var(--fg-2)" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label="移除"
            style={iconButtonStyle(30)}
          >
            <Icon name="trash" size={14} stroke="var(--fg-3)" />
          </button>
        </div>
      )}
    </div>
  );
}

function DoneFieldsSummary({
  fields,
  categories,
}: {
  fields: EditableFields;
  categories: ReturnType<typeof useStore.getState>['categories']['items'];
}): JSX.Element {
  const cat = categories.find((c) => c.id === fields.categoryId);
  const sub = categories.find((c) => c.id === fields.subCategoryId);
  const catColor = cat ? `var(${cat.color_token})` : null;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              font: '600 15px var(--font-cjk)',
              color: 'var(--fg-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {fields.merchant || '(未填店家)'}
          </div>
        </div>
        <div
          style={{
            font: '700 16px var(--font-num)',
            color: 'var(--fg-1)',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatAmount(fields.amountMinor, fields.currency)}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
          marginTop: 8,
        }}
      >
        {cat && catColor && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 999,
              background: catColor + '22',
              border: `0.5px solid ${catColor}55`,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: catColor,
                display: 'inline-block',
              }}
            />
            <span
              style={{
                font: '500 11px var(--font-cjk)',
                color: 'var(--fg-1)',
              }}
            >
              {cat.name}
              {sub ? ` · ${sub.name}` : ''}
            </span>
          </div>
        )}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            borderRadius: 999,
            background: 'var(--cream-200)',
          }}
        >
          <Icon name="calendar" size={10} stroke="var(--fg-3)" />
          <span
            style={{
              font: '500 11px var(--font-cjk)',
              color: 'var(--fg-2)',
            }}
          >
            {dayPart(fields.date).slice(5).replace('-', '/')}
          </span>
        </div>
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Per-image edit sheet
// ───────────────────────────────────────────────────────────────────────────

interface ReceiptEditSheetProps {
  item: ReceiptItem;
  fields: EditableFields;
  categories: ReturnType<typeof useStore.getState>['categories']['items'];
  books: ReturnType<typeof useStore.getState>['books']['items'];
  onSave: (patch: Partial<EditableFields>) => void;
  onClose: () => void;
}

function ReceiptEditSheet({
  item,
  fields,
  categories,
  books,
  onSave,
  onClose,
}: ReceiptEditSheetProps): JSX.Element {
  // `fields.date` carries a merged `YYYY-MM-DDTHH:MM`; the date input only
  // understands the day part. We edit the day here and preserve the original
  // time on save so editing a date never silently drops the time component.
  const [date, setDate] = useState(dayPart(fields.date));
  const [amountMajor, setAmountMajor] = useState(
    String((fields.amountMinor / 100).toFixed(2)),
  );
  const [currency, setCurrency] = useState<SupportedCurrency>(fields.currency);
  const [categoryId, setCategoryId] = useState(fields.categoryId);
  const [subCategoryId, setSubCategoryId] = useState(fields.subCategoryId);
  const [bookId, setBookId] = useState(fields.bookId);
  // Editable note — defaults to the OCR-built block but the user can
  // freely overwrite.
  const [note, setNote] = useState(fields.note);

  // Foreign-currency convenience toggle. When OCR detected a currency
  // different from the user's primary, default to "原幣" (keep tx in
  // detected currency, app FX-converts on display) AND offer a one-tap
  // "convert to <primary>" that bakes the FX-converted minor amount in
  // primary currency into the saved row.
  const primaryCurrency = useStore(
    (s) => (s.metaCache.primaryCurrency || 'TWD').toUpperCase(),
  ) as SupportedCurrency;
  const isForeign = currency.toUpperCase() !== primaryCurrency;
  const [convertToPrimary, setConvertToPrimary] = useState(false);

  const handleSave = (): void => {
    const major = parseFloat(amountMajor);
    const minorInDetected = Number.isFinite(major) ? Math.round(major * 100) : 0;
    // When the user opts in to "換算成 {primary}", we bake the converted
    // amount + primary currency code into the saved row. Otherwise we
    // keep the foreign currency intact and let the rest of the app FX-
    // convert at display time (BalanceCard / Analytics already do).
    const finalCurrency: SupportedCurrency =
      isForeign && convertToPrimary ? primaryCurrency : currency;
    const finalMinor =
      isForeign && convertToPrimary
        ? convertMinor(minorInDetected, currency, primaryCurrency)
        : minorInDetected;

    const lines = note.trim().split('\n');
    const firstLine = lines[0] || '';

    onSave({
      merchant: firstLine,
      // Re-attach the preserved time component (fall back to capture time if
      // somehow absent) so the saved transaction keeps `YYYY-MM-DDTHH:MM`.
      date: joinDateTime(date, timePart(fields.date) || nowHm()),
      amountMinor: finalMinor,
      currency: finalCurrency,
      categoryId,
      subCategoryId,
      bookId,
      note: note.trim(),
    });
  };

  return (
    <BottomSheet open onClose={onClose} title="確認辨識結果">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {item.thumbnailDataUrl && (
          <div
            style={{
              alignSelf: 'center',
              width: 140,
              height: 180,
              borderRadius: 12,
              overflow: 'hidden',
              border: '0.5px solid var(--border-1)',
              boxShadow: 'var(--shadow-1)',
              background: `center/cover no-repeat url(${item.thumbnailDataUrl})`,
            }}
            aria-label="收據縮圖"
          />
        )}
        <Field label="金額">
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <select
              aria-label="幣別"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}
              style={{ ...inputStyle(), width: 'auto' }}
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              aria-label="金額"
              type="number"
              inputMode="decimal"
              value={amountMajor}
              onChange={(e) => setAmountMajor(e.target.value)}
              style={inputStyle()}
            />
          </div>
        </Field>
        {isForeign && (
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(199,154,75,0.10)',
              border: '0.5px solid rgba(199,154,75,0.30)',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
            data-testid="ccy-convert-toggle"
          >
            <div
              style={{
                font: '400 12.5px/1.5 var(--font-cjk)',
                color: 'var(--fg-2)',
                flex: 1,
              }}
            >
              {convertToPrimary
                ? `將以 ${primaryCurrency} 記錄（換算 ${currency} → ${primaryCurrency}）`
                : `保留原幣 ${currency}（顯示時自動換算成 ${primaryCurrency}）`}
            </div>
            <button
              type="button"
              onClick={() => setConvertToPrimary((v) => !v)}
              aria-pressed={convertToPrimary}
              aria-label={convertToPrimary ? '改為記錄原幣' : `換算成 ${primaryCurrency}`}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                background: convertToPrimary ? 'var(--accent)' : 'var(--bg-surface)',
                color: convertToPrimary ? '#fff' : 'var(--fg-1)',
                border: '1px solid var(--border-1)',
                font: '500 12px var(--font-cjk)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {convertToPrimary ? '原幣' : `換 ${primaryCurrency}`}
            </button>
          </div>
        )}

        <Field label="日期">
          <input
            aria-label="日期"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={inputStyle()}
          />
        </Field>
        <Field label="帳本">
          <select
            aria-label="帳本"
            value={bookId}
            onChange={(e) => setBookId(e.target.value)}
            style={inputStyle()}
          >
            {books
              .filter((b) => !b.deleted_at)
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="分類">
          <CategoryPicker
            kind="expense"
            categories={categories}
            selectedId={categoryId || null}
            selectedSubId={subCategoryId || null}
            onChange={(catId, subId) => {
              setCategoryId(catId);
              setSubCategoryId(subId ?? '');
            }}
          />
        </Field>
        <Field label="備註">
          <textarea
            aria-label="備註"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={Math.max(3, note.split('\n').length)}
            placeholder={`{商家}\n- 商品 x數量 @價錢`}
            style={{
              ...inputStyle(),
              minHeight: 80,
              resize: 'vertical',
              fontFamily: 'var(--font-cjk)',
              lineHeight: 1.5,
            }}
          />
        </Field>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--border-1)',
              cursor: 'pointer',
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-2)',
            }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              background: 'var(--accent)',
              border: 0,
              cursor: 'pointer',
              font: '600 14px var(--font-cjk)',
              color: '#fff',
            }}
          >
            套用
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          marginBottom: 6,
          padding: '0 2px',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function inputStyle(): CSSProperties {
  return {
    width: '100%',
    padding: '12px 14px',
    border: '0.5px solid var(--border-1)',
    borderRadius: 12,
    background: 'var(--bg-surface)',
    font: '500 14px var(--font-cjk)',
    color: 'var(--fg-1)',
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function iconButtonStyle(size = 36): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: size / 2,
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
  };
}
